if (!window.MarketSwitch) {
  (() => {
    const HANDOFF_PARAM = "_locale_switch";
    const SUPPORTED_COUNTRIES = ["US", "CA"];
    const SESSION_PREFIX = "geolocation_prompt:v2";
    const memorySuppressions = new Set();
    let pendingSwitch = null;

    function normalizeCountry(value) {
      const country = String(value || "").toUpperCase();
      return SUPPORTED_COUNTRIES.includes(country) ? country : null;
    }

    function normalizeIsoCountry(value) {
      const country = String(value || "").toUpperCase();
      return /^[A-Z]{2}$/.test(country) ? country : null;
    }

    function root() {
      return document.querySelector("market-switch");
    }

    function activeCountry() {
      return normalizeIsoCountry(root()?.dataset.activeCountry);
    }

    function sessionKey(country) {
      return `${SESSION_PREFIX}:${country}`;
    }

    function suppressAdvice(countryValue) {
      const country = normalizeCountry(countryValue);

      if (!country) {
        return { ok: false };
      }

      memorySuppressions.add(country);
      return window.ThemeStorage?.setString?.(sessionKey(country), "true", "sessionStorage") || {
        ok: false,
      };
    }

    function shouldSuppressAdvice(countryValue) {
      const country = normalizeCountry(countryValue);

      if (!country) {
        return false;
      }

      return (
        memorySuppressions.has(country) ||
        window.ThemeStorage?.getString?.(sessionKey(country), null, "sessionStorage") === "true"
      );
    }

    function ensureInput(form, name) {
      let input = form?.querySelector(`input[name="${name}"]`);

      if (!input && form) {
        input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        form.append(input);
      }

      return input;
    }

    function resolveForm(form) {
      if (form?.isConnected) {
        return form;
      }

      return form?.id ? document.getElementById(form.id) : null;
    }

    function resolveOpener(opener) {
      if (opener?.isConnected) {
        return opener;
      }

      const controlledId = opener?.getAttribute?.("aria-controls");

      if (!controlledId) {
        return document.activeElement;
      }

      return Array.from(document.querySelectorAll("[aria-controls]")).find(
        (candidate) => candidate.getAttribute("aria-controls") === controlledId,
      ) || document.activeElement;
    }

    function prepareCountrySubmit(form, countryValue) {
      const destination = normalizeCountry(countryValue);
      const current = activeCountry();

      if (!form || !destination) {
        return false;
      }

      const countryInput = ensureInput(form, "country_code");
      const returnToInput = ensureInput(form, "return_to");
      const returnUrl = new URL(window.location.href);

      if (!countryInput || !returnToInput) {
        return false;
      }

      countryInput.value = destination;
      returnUrl.searchParams.set(HANDOFF_PARAM, destination);
      returnToInput.value = `${returnUrl.pathname}${returnUrl.search}${returnUrl.hash}`;

      if (current) {
        suppressAdvice(current);
      }

      return true;
    }

    function countryDetails(country) {
      const option = document.querySelector(
        `[data-country-option][data-value="${country}"]`,
      );
      const switchRoot = root();
      const isActiveCountry = country === activeCountry();

      return {
        name:
          option?.dataset.countryName ||
          (isActiveCountry ? switchRoot?.dataset.activeCountryName : null) ||
          country,
        currency:
          option?.dataset.countryCurrency ||
          (isActiveCountry ? switchRoot?.dataset.activeCurrency : null) ||
          "",
      };
    }

    function interpolate(value, replacements) {
      return String(value || "").replace(/\[([a-z_]+)\]/g, (match, key) => {
        return replacements[key] || match;
      });
    }

    async function cartState() {
      const cartUrl = `${window.Shopify?.routes?.root || "/"}cart.js`;

      try {
        const response = await fetch(cartUrl, {
          headers: { Accept: "application/json" },
        });

        if (!response.ok) {
          return { known: false, itemCount: null };
        }

        const cart = await response.json();
        const itemCount = Number(cart?.item_count);

        return Number.isFinite(itemCount)
          ? { known: true, itemCount }
          : { known: false, itemCount: null };
      } catch (_error) {
        return { known: false, itemCount: null };
      }
    }

    function hideConfirmation() {
      const modal = document.querySelector("#MarketSwitchConfirmation");
      pendingSwitch = null;
      modal?.hide?.();
    }

    function showConfirmation(form, destination, source, opener) {
      const switchRoot = root();
      const modal = document.querySelector("#MarketSwitchConfirmation");
      const current = activeCountry();

      if (!switchRoot || !modal || !current) {
        return false;
      }

      const currentDetails = countryDetails(current);
      const destinationDetails = countryDetails(destination);
      const replacements = {
        active_country: currentDetails.name,
        target_country: destinationDetails.name,
      };

      modal.querySelector("[data-market-switch-title]").textContent = switchRoot.dataset.confirmTitle;
      modal.querySelector("[data-market-switch-body]").textContent = interpolate(
        switchRoot.dataset.confirmBody,
        replacements,
      );
      modal.querySelector("[data-market-switch-keep]").textContent = interpolate(
        switchRoot.dataset.keepCart,
        replacements,
      );
      modal.querySelector("[data-market-switch-confirm]").textContent = interpolate(
        switchRoot.dataset.confirmSwitch,
        replacements,
      );

      pendingSwitch = { form, destination, source };
      modal.show(resolveOpener(opener));
      trapFocus(modal, modal.querySelector("[data-market-switch-keep]"));
      return true;
    }

    function submitPrepared(form, destination) {
      const currentForm = resolveForm(form);

      if (prepareCountrySubmit(currentForm, destination)) {
        currentForm.submit();
      }
    }

    async function requestCountrySwitch(form, countryValue, source = "unknown", opener = null) {
      const destination = normalizeCountry(countryValue);
      const current = activeCountry();

      if (!form || !destination || !current || destination === current) {
        return;
      }

      const cart = await cartState();
      const currentForm = resolveForm(form);

      if (!currentForm) {
        return;
      }

      if (!cart.known || cart.itemCount > 0) {
        showConfirmation(currentForm, destination, source, opener);
        return;
      }

      submitPrepared(currentForm, destination);
    }

    function consumeHandoff() {
      const current = activeCountry();
      const url = new URL(window.location.href);
      const marker = url.searchParams.get(HANDOFF_PARAM);
      const destination = normalizeCountry(url.searchParams.get(HANDOFF_PARAM));

      if (!marker) {
        return false;
      }

      const isValidDestination = Boolean(current && destination === current);

      if (isValidDestination) {
        suppressAdvice(current);
      }

      url.searchParams.delete(HANDOFF_PARAM);
      window.history.replaceState(
        window.history.state,
        "",
        `${url.pathname}${url.search}${url.hash}`,
      );
      return isValidDestination;
    }

    function initialize() {
      const modal = document.querySelector("#MarketSwitchConfirmation");
      const keepButton = modal?.querySelector("[data-market-switch-keep]");
      const confirmButton = modal?.querySelector("[data-market-switch-confirm]");

      keepButton?.addEventListener("click", hideConfirmation);
      modal?.addEventListener("keydown", (event) => {
        if (event.code.toUpperCase() !== "ESCAPE") {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        hideConfirmation();
      });
      confirmButton?.addEventListener("click", () => {
        if (!pendingSwitch) {
          return;
        }

        const { form, destination } = pendingSwitch;
        pendingSwitch = null;
        submitPrepared(form, destination);
      });

      consumeHandoff();
    }

    window.MarketSwitch = {
      requestCountrySwitch,
      prepareCountrySubmit,
      shouldSuppressAdvice,
      suppressAdvice,
      handoffParam: HANDOFF_PARAM,
    };

    initialize();
  })();
}
